import { useState } from 'react';
import {
  Container,
  List,
  Input,
  Button,
  Image,
  Loader,
} from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Navigation from '@plone/volto/components/theme/Navigation/Navigation';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import upKeySVG from '@plone/volto/icons/up-key.svg';

import { FacebookLogo } from '../../../../components/Icons/FacebookLogo';
import { TwitterLogo } from '../../../../components/Icons/TwitterLogo';
import { YoutubeLogo } from '../../../../components/Icons/YoutubeLogo';
import { InstagramLogo } from '../../../../components/Icons/InstagramLogo';
import messages from '../../../../components/theme/footerMessages';
import { getLanguagePrefixedPath } from '../../../../helpers/homepageUrl';

const handleScrollup = () => {
  if (typeof window !== 'undefined') {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }
};

const Footer = () => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const locale = useSelector((state) => state.intl.locale);
  const [email, setEmail] = useState('');
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const legalPath = (path) => getLanguagePrefixedPath(pathname, locale, path);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setLoading(true);

    try {
      const response = await fetch('/subscribe-newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setStatus('success');
        setEmail('');
        setPrivacyPolicy(false);
      } else {
        setStatus('error');
      }
    } catch (_err) {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <hr
        className="full-width-separator bottom-separator"
        style={{ marginBottom: '50px' }}
      />
      <Container className="footer-container">
        <div className="footer-top-container">
          <Navigation pathname={pathname} footer />
          <div className="footer-vertical-separator" />
          <div className="footer-subscribe-container">
            <p className="footer-subscribe-title">
              {intl.formatMessage(messages.subscribeTitle)}
            </p>
            <p className="footer-subscribe-subtitle">
              {intl.formatMessage(messages.subscribeSubtitle)}
            </p>
            <form onSubmit={handleSubmit}>
              <Input
                className="footer-subscribe-email"
                type="email"
                name="EMAIL"
                placeholder={intl.formatMessage(messages.emailPlaceholder)}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
              <div className="footer-subscribe-submit">
                <Input
                  className="footer-subscribe-button"
                  type="submit"
                  value={
                    loading
                      ? intl.formatMessage(messages.sending)
                      : intl.formatMessage(messages.send)
                  }
                  name="subscribe"
                  disabled={loading}
                />
                <Input
                  className="footer-subscribe-check"
                  type="checkbox"
                  name="privacy_policy"
                  required
                  checked={privacyPolicy}
                  onChange={(e) => setPrivacyPolicy(e.target.checked)}
                  disabled={loading}
                />
                <span>{intl.formatMessage(messages.privacyAccept)}</span>
              </div>
              {loading && (
                <div>
                  <Loader active inline size="small" />
                  {intl.formatMessage(messages.sending)}
                </div>
              )}
              {status === 'success' && (
                <div>{intl.formatMessage(messages.subscribeSuccess)}</div>
              )}
              {status === 'error' && (
                <div>{intl.formatMessage(messages.subscribeError)}</div>
              )}
            </form>
          </div>
        </div>
        <div className="footer-bottom-container">
          <div className="footer-links-container">
            <List horizontal>
              <List.Item className="first" href={legalPath('/legal')}>
                {intl.formatMessage(messages.legalNotice)}
              </List.Item>
              <List.Item href={legalPath('/politica-privacitat')}>
                {intl.formatMessage(messages.privacyPolicy)}
              </List.Item>
              <List.Item href={legalPath('/cookies')}>
                {intl.formatMessage(messages.cookies)}
              </List.Item>
              <List.Item href={legalPath('/sitemap')}>
                {intl.formatMessage(messages.sitemap)}
              </List.Item>
            </List>
            <p>
              &copy; {new Date().getFullYear()}{' '}
              {intl.formatMessage(messages.copyright)}
            </p>
          </div>
          <div className="footer-logos-social-container">
            <div className="footer-logos-container">
              <a
                href="http://www.althaia.cat/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image
                  className="footer-logo-althaia"
                  src="/logo_althaia_horitzontal.jpg"
                  alt="Althaia"
                  width={110}
                  height={26}
                />
              </a>
              <a href={legalPath('/la-clinica/certificacio-efqm')}>
                <Image
                  className="footer-logo-efqm"
                  src="/logo-efqm.png"
                  alt={intl.formatMessage(messages.efqmLogoAlt)}
                />
              </a>
              <a href={legalPath('/la-clinica/lliure-de-fum')}>
                <Image
                  className="footer-logo-lliuredefum"
                  src="/logo-lliuredefum.png"
                  alt={intl.formatMessage(messages.smokeFreeLogoAlt)}
                />
              </a>
            </div>
            <div className="footer-social-container">
              <p className="footer-social-heading">
                {intl.formatMessage(messages.followUs)}
              </p>
              <List horizontal>
                <List.Item
                  href="http://www.facebook.com/CSJManresa"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button circular icon className="footer-social-icon">
                    <FacebookLogo />
                  </Button>
                </List.Item>
                <List.Item
                  href="https://twitter.com/CSJ_Manresa"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    circular
                    icon
                    className="footer-social-icon twitter-icon"
                  >
                    <TwitterLogo />
                  </Button>
                </List.Item>
                <List.Item
                  href="https://www.youtube.com/user/clinicasantjosep"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button circular icon className="footer-social-icon">
                    <YoutubeLogo />
                  </Button>
                </List.Item>
                <List.Item
                  href="https://www.instagram.com/csjmanresa/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button circular icon className="footer-social-icon">
                    <InstagramLogo />
                  </Button>
                </List.Item>
              </List>
            </div>
            <Button
              className="footer-scrollup"
              circular
              icon
              onClick={handleScrollup}
              aria-label={intl.formatMessage(messages.scrollToTop)}
            >
              <Icon name={upKeySVG} size="18px" />
            </Button>
          </div>
        </div>
      </Container>
    </>
  );
};

export default Footer;
